import React, {useRef} from 'react';
import {Link} from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {objectToArray} from 'utils/objects';
import ModalImage from 'components/ModalImage';
import {ValidationErrorAlert, ReviewAlert} from './Alerts.jsx';
import AopsCard from 'components/forms/uscgaops/sections/AopsCard';
import {PrintFieldValue} from 'components';
import useGetFiles from 'hooks/useGetFiles';
import {PBold} from 'components/TypographyShortcuts';
import {StandAloneVesselCard} from 'components/forms/vessel/VesselCard.jsx';
import {mapFieldLabels} from 'components/utils/field-label-mappings';
import Show from 'components/utils/Show';

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        '& > *': {
            marginBottom: '1em',
        },
    },
    control: {
        padding: theme.spacing(2),
    },
    prompt: {
        color: theme.palette.primary.dark,
        fontWeight: 'bold',
    },
    table: {
        background: theme.palette.gray.light,
    },
    tableHeader: {
        background: theme.palette.primary.main,
    },
    tableHeaderCell: {
        color: 'white',
        fontWeight: 'bold',
    },
    tableRowError: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.getContrastText(theme.palette.error.light),
    },
}));

const FieldRow = ({field, fieldLabelMappings, fieldErrorMappings, jsonMap}) => {
    const classes = useStyles();
    const errorMessage = fieldErrorMappings?.[field.key];
    if (field.key === 'vessel_id') {
        return (
            <TableRow className={errorMessage ? classes.tableRowError : ''}>
                <TableCell
                    className={`${classes.fieldLabel} ${
                        errorMessage ? classes.tableRowError : ''
                    }`}
                    data-cy={`form-review-${field.key}`}
                    colSpan={2}
                >
                    <PBold>Vessel</PBold>
                    <div style={{display: 'flex', justifyContent: 'center'}}>
                        <StandAloneVesselCard vesselId={field.value} />
                    </div>
                </TableCell>
            </TableRow>
        );
    } else {
        return (
            <React.Fragment>
                <TableRow className={errorMessage ? classes.tableRowError : ''}>
                    <TableCell
                        className={`${classes.fieldLabel} ${
                            errorMessage ? classes.tableRowError : ''
                        }`}
                        data-cy={`form-review-${field.key}`}
                    >
                        <PBold>
                            {fieldLabelMappings[field.key] || field.key}
                        </PBold>
                    </TableCell>
                    <TableCell
                        data-cy={`form-review-${field.key}-value`}
                        className={errorMessage ? classes.tableRowError : ''}
                    >
                        <PrintFieldValue
                            map={jsonMap}
                            fieldname={field.key}
                            value={field.value}
                        />
                    </TableCell>
                </TableRow>
                {errorMessage && (
                    <TableRow
                        className={errorMessage ? classes.tableRowError : ''}
                    >
                        <TableCell
                            className={
                                errorMessage ? classes.tableRowError : ''
                            }
                        >
                            {errorMessage}
                        </TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                )}
            </React.Fragment>
        );
    }
};

const ReviewComponent = ({
    state,
    showFormInfo,
    fields,
    fieldLabelMappings,
    fieldErrorMappings,
    generalErrors,
    children,
    isEnd,
    allowEdit,
    aops = false,
    jsonMap,
    isDocumentDetail,
}) => {
    const classes = useStyles();
    const containerRef = useRef(null);
    const labelMap = fieldLabelMappings || mapFieldLabels(fields);

    const {files, isLoading} = useGetFiles(
        state?.user_document_post_response?.user_images?.map(image => image.id)
    );
    const hasErrors =
        (fieldErrorMappings && Object.keys(fieldErrorMappings).length > 0) ||
        (generalErrors && generalErrors.length);

    const fieldsToShow = objectToArray(state || {})?.filter(
        field =>
            (field.value || field.value === 0) &&
            field.key !== 'user_document_post_response' &&
            labelMap[field.key]
    );

    return (
        <>
            <Grid container justifyContent='space-around'>
                <Grid item sm={10} md={8}>
                    {children}
                </Grid>
            </Grid>
            {isEnd ? (
                <Grid
                    container
                    direction='row'
                    className={classes.root}
                    justifyContent='space-evenly'
                    alignItems='center'
                >
                    {isLoading ? (
                        <CircularProgress />
                    ) : (
                        files?.map((image, index) => (
                            <Grid
                                item
                                key={`review-document-${index}`}
                                ref={containerRef}
                            >
                                <ModalImage
                                    alt={`uploaded document page ${index + 1}`}
                                    src={image.thumb_signed_url}
                                    fullSrc={image.signed_url}
                                    filename={image.document_filename}
                                    mimeType={image.mime_type}
                                    ref={containerRef}
                                    image_id={image.id}
                                />
                            </Grid>
                        ))
                    )}
                </Grid>
            ) : null}
            {showFormInfo && isEnd && (
                <Box component='div' m={2} className={classes.prompt}>
                    {hasErrors
                        ? ValidationErrorAlert(generalErrors)
                        : ReviewAlert(
                              aops
                                  ? {
                                        body: 'Double check these numbers and then hit Validate at the bottom of the screen!',
                                    }
                                  : {}
                          )}
                </Box>
            )}
            <Grid container justifyContent='center'>
                <Grid item xs={12} sm={10} lg={8}>
                    <Show
                        condition={isDocumentDetail && state?.ssl_id}
                    >
                        <Button
                            variant='contained'
                            color='primary'
                            component={Link}
                            to={`/document/ssl/${state.ssl_id}`}
                            style={{margin: '1em 0'}}
                        >
                            View linked SSL
                        </Button>
                    </Show>
                    {aops ? (
                        <AopsCard state={state} allowEdit={allowEdit} />
                    ) : (
                        <TableContainer component={Paper}>
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow className={classes.tableHeader}>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            style={{width: '50%'}}
                                        >
                                            Field
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            style={{width: '50%'}}
                                        >
                                            Value
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fieldsToShow?.map(field => (
                                        <FieldRow
                                            field={field}
                                            fieldLabelMappings={labelMap}
                                            fieldErrorMappings={
                                                fieldErrorMappings
                                            }
                                            jsonMap={fields?._jsonMap}
                                            key={field.key}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default ReviewComponent;
